import { getErrorMessage } from '../utils/serviceUtils';
import React from 'react';
import type { PluginListenerHandle } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/useAuth';
import { isNativeApp } from '../utils/appLinks';
import { logger } from '../utils/logger';
import {
  clearStoredPushToken,
  disableStoredPushDevice,
  extractPendingPushAction,
  persistPendingPushAction,
  persistPushToken,
  readPendingPushActions,
  readStoredPushToken,
  resolvePushNotificationPath,
  upsertMyPushDevice,
} from '../services/PushDeviceService';

export default function PushNotificationsBridge() {
  const navigate = useNavigate();
  const location = useLocation();
  const { initialized, isAuthenticated, user } = useAuth();
  const currentPathRef = React.useRef('');
  const listenersBoundRef = React.useRef(false);
  const previousAuthenticatedRef = React.useRef(false);
  const isAuthenticatedRef = React.useRef(false);

  React.useEffect(() => {
    currentPathRef.current = `${location.pathname}${location.search}${location.hash}`;
  }, [location.hash, location.pathname, location.search]);

  React.useEffect(() => {
    isAuthenticatedRef.current = initialized && isAuthenticated;
  }, [initialized, isAuthenticated]);

  React.useEffect(() => {
    if (!isNativeApp() || listenersBoundRef.current) return;

    let cancelled = false;
    const handles: PluginListenerHandle[] = [];
    listenersBoundRef.current = true;

    const bindListeners = async () => {
      try {
        handles.push(await PushNotifications.addListener('registration', async ({ value }) => {
          if (cancelled) return;
          if (!isAuthenticatedRef.current) {
            persistPushToken(value);
            return;
          }

          try {
            await upsertMyPushDevice(value);
          } catch (error) {
            logger.warn('PUSH_REGISTRATION_SYNC_FAILED', {
              message: getErrorMessage(error),
            });
          }
        }));

        handles.push(await PushNotifications.addListener('registrationError', (error) => {
          logger.warn('PUSH_REGISTRATION_ERROR', error);
        }));

        handles.push(await PushNotifications.addListener('pushNotificationReceived', (notification) => {
          logger.info('PUSH_NOTIFICATION_RECEIVED', {
            id: notification.id ?? null,
            title: notification.title ?? null,
          });
        }));

        handles.push(await PushNotifications.addListener('pushNotificationActionPerformed', ({ actionId, notification }) => {
          const pendingAction = extractPendingPushAction(notification, actionId);
          if (pendingAction) {
            persistPendingPushAction(pendingAction);
          }
          const path = resolvePushNotificationPath(notification);
          if (!path || currentPathRef.current === path) return;
          currentPathRef.current = path;
          navigate(path, { replace: true });
        }));
      } catch (error) {
        logger.warn('PUSH_LISTENER_BIND_FAILED', {
          message: getErrorMessage(error),
        });
      }
    };

    void bindListeners();

    return () => {
      cancelled = true;
      listenersBoundRef.current = false;
      handles.forEach((handle) => {
        void handle.remove();
      });
    };
  }, [navigate]);

  React.useEffect(() => {
    if (!isNativeApp() || !initialized) return;

    let cancelled = false;
    const wasAuthenticated = previousAuthenticatedRef.current;
    previousAuthenticatedRef.current = isAuthenticated;

    const ensureNativePushRegistration = async () => {
      if (isAuthenticated) {
        try {
          logger.info('PUSH_REGISTRATION_FLOW_START', {
            platform: 'native',
            userId: user?.id ?? null,
          });

          const storedPushToken = readStoredPushToken();
          if (storedPushToken) {
            logger.info('PUSH_STORED_TOKEN_FOUND', {
              hasStoredPushToken: true,
              userId: user?.id ?? null,
            });
            await upsertMyPushDevice(storedPushToken);
          }

          const permissions = await PushNotifications.checkPermissions();
          let receivePermission = permissions.receive;

          logger.info('PUSH_PERMISSION_STATUS', {
            receivePermission,
            userId: user?.id ?? null,
          });

          if (receivePermission === 'prompt' || receivePermission === 'prompt-with-rationale') {
            const requested = await PushNotifications.requestPermissions();
            receivePermission = requested.receive;

            logger.info('PUSH_PERMISSION_REQUEST_RESULT', {
              receivePermission,
              userId: user?.id ?? null,
            });
          }

          if (cancelled) return;

          if (receivePermission === 'granted') {
            logger.info('PUSH_REGISTER_ATTEMPT', {
              userId: user?.id ?? null,
            });
            await PushNotifications.register();
            logger.info('PUSH_REGISTER_CALLED', {
              userId: user?.id ?? null,
            });
            return;
          }

          logger.info('PUSH_PERMISSION_NOT_GRANTED', {
            receivePermission,
            userId: user?.id ?? null,
          });
        } catch (error) {
          logger.warn('PUSH_REGISTRATION_FLOW_FAILED', {
            message: getErrorMessage(error),
            userId: user?.id ?? null,
          });
        }
        return;
      }

      if (!wasAuthenticated) {
        return;
      }

      try {
        await disableStoredPushDevice();
      } catch (error) {
        logger.warn('PUSH_DISABLE_ON_SIGNOUT_FAILED', {
          message: getErrorMessage(error),
        });
      }

      try {
        await PushNotifications.unregister();
      } catch (error) {
        logger.warn('PUSH_UNREGISTER_FAILED', {
          message: getErrorMessage(error),
        });
      }

      clearStoredPushToken();
    };

    void ensureNativePushRegistration();

    return () => {
      cancelled = true;
    };
  }, [initialized, isAuthenticated, user?.id]);

  React.useEffect(() => {
    if (!initialized || !isAuthenticated) return;
    const actions = readPendingPushActions();
    if (actions.length === 0) return;

    const latestPathAction = [...actions].reverse().find((action) => action.type === 'friend_request_accept' || action.type === 'friend_request_reject');
    if (!latestPathAction) return;
    const targetPath = '/account/social';
    if (currentPathRef.current === targetPath) return;
    currentPathRef.current = targetPath;
    navigate(targetPath, { replace: false });
  }, [initialized, isAuthenticated, navigate]);

  return null;
}
